import {useAxiosClient} from "~/use/axios/client";
import {
    GmModelLibraryModelsTrackDamageOverviewQuery,
    type GmModelLibraryModelsTrackDamageTrackDamageUpdateInputDto, TrackDamageApi
} from "~/gen/openapi/sblService";
import {MS_MINUTE} from "~/constants/time";
import {keepPreviousData, useMutation, useQuery, UseQueryOptions} from "@tanstack/vue-query";
import {extractErrorMessagesFromErrorOrResponse} from "~/helpers/errors";
import {queryClient} from "~/plugins/vue-query";
import {TRACK_DAMAGE_REPAIR_STATUS_REJECTED} from "~/constants/trackDamage";
import {User} from "~/models/auth";

export const useTrackDamageService = () => {
    const { axiosClient } = useAxiosClient()
    const trackDamageApi = new TrackDamageApi(undefined, '', axiosClient.value)

    const defaultQueryOptions = {
        staleTime: 5 * MS_MINUTE,
        refetchOnWindowFocus: false,
        placeholderData: keepPreviousData,
    } as UseQueryOptions<any>

    const getOperationsWithTrackDamageQueryOptions = (currentUser: Ref<User | undefined>, filter: GmModelLibraryModelsTrackDamageOverviewQuery = {Managers: ['MYSELF']}) => {
        return {
            queryKey: ['trackDamageGetTrackDamageOverview', filter],
            queryFn: async () => {
                try {
                    if (!currentUser.value?.Id) {
                        return Promise.reject('Missing user')
                    }
                    const transformedFilter: GmModelLibraryModelsTrackDamageOverviewQuery = !!filter ? {
                        ...filter,
                        Managers: filter?.Managers?.map(manager => manager === 'MYSELF' ? currentUser.value?.Id : manager) as string[]
                    } : { Managers: [currentUser.value?.Id] } as GmModelLibraryModelsTrackDamageOverviewQuery

                    return (await trackDamageApi.trackDamageGetTrackDamageOverview(transformedFilter))?.data?.ForestOperationContracts || []
                } catch (error) {
                    throw new Error(extractErrorMessagesFromErrorOrResponse(error)[0])
                }
            },
            enabled: computed(() => !!currentUser.value?.Id),
            ...defaultQueryOptions
        }
    }

    const operationsWithTrackDamageQuery = (currentUser: Ref<User | undefined>, filter?: GmModelLibraryModelsTrackDamageOverviewQuery) => useQuery(getOperationsWithTrackDamageQueryOptions(currentUser, filter))

    const confirmOperationMutation = () => useMutation({
        mutationFn: async (operationConfirmation: GmModelLibraryModelsTrackDamageTrackDamageUpdateInputDto) => {
            await trackDamageApi.trackDamageUpdateTrackDamage(operationConfirmation.ForestOperationContractId!, operationConfirmation)
        },
        onSettled: () => {
            queryClient.invalidateQueries({queryKey: ['trackDamageGetTrackDamageOverview']})
        }
    })

    const declineOperationMutation = () => useMutation({
        mutationFn: async ({forestOperationContractId, statusDescription}: {forestOperationContractId: string, statusDescription: string}) => {
            await trackDamageApi.trackDamageUpdateTrackDamage(forestOperationContractId, {
                ForestOperationContractId: forestOperationContractId,
                TrackDamageStatusDescription: statusDescription,
                // @ts-ignore
                Status: TRACK_DAMAGE_REPAIR_STATUS_REJECTED
            })
        },
        onSettled: () => {
            queryClient.invalidateQueries({queryKey: ['trackDamageGetTrackDamageOverview']})
        }
    })

    return {
        getOperationsWithTrackDamageQueryOptions,
        operationsWithTrackDamageQuery,
        confirmOperationMutation,
        declineOperationMutation,
    }
}